import { useState, useEffect } from 'react';
import axios from 'axios';
import { Sidebar } from '../components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Loader2, 
  DollarSign, 
  Plus, 
  Edit2, 
  Trash2,
  Calendar,
  Clock,
  Building2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { formatCurrency, cn } from '../lib/utils';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BudgetManagementPage() {
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [budgets, setBudgets] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  
  // Dialog states
  const [showSingleDialog, setShowSingleDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [singleForm, setSingleForm] = useState({
    date: new Date().toISOString().split('T')[0],
    sales_budget: '',
    labor_hours_budget: ''
  });
  const [bulkForm, setBulkForm] = useState({
    start_date: '',
    end_date: '',
    daily_sales_budget: '',
    daily_labor_hours_budget: ''
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchBudgets();
    }
  }, [selectedLocation, weekOffset]);

  const fetchLocations = async () => {
    try {
      const res = await axios.get(`${API}/locations`);
      setLocations(res.data);
      if (res.data.length > 0) {
        setSelectedLocation(res.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const fetchBudgets = async () => {
    try {
      const startOfWeek = new Date();
      startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + (weekOffset * 7));
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(endOfWeek.getDate() + 6);
      
      const res = await axios.get(`${API}/budgets`, {
        params: {
          location_id: selectedLocation,
          start_date: startOfWeek.toISOString().split('T')[0],
          end_date: endOfWeek.toISOString().split('T')[0]
        }
      });
      setBudgets(res.data);
    } catch (error) {
      console.error('Failed to fetch budgets:', error);
    }
  };

  const handleCreateBudget = async () => {
    setSubmitting(true);
    try {
      if (editingBudget) {
        await axios.put(`${API}/budgets/${editingBudget.id}`, {
          location_id: selectedLocation,
          date: singleForm.date,
          sales_budget: parseFloat(singleForm.sales_budget),
          labor_hours_budget: parseFloat(singleForm.labor_hours_budget)
        });
        toast.success('Budget updated successfully');
      } else {
        await axios.post(`${API}/budgets`, {
          location_id: selectedLocation,
          date: singleForm.date,
          sales_budget: parseFloat(singleForm.sales_budget),
          labor_hours_budget: parseFloat(singleForm.labor_hours_budget)
        });
        toast.success('Budget created successfully');
      }
      setShowSingleDialog(false);
      setEditingBudget(null);
      setSingleForm({ date: new Date().toISOString().split('T')[0], sales_budget: '', labor_hours_budget: '' });
      fetchBudgets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save budget');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkCreate = async () => {
    setSubmitting(true);
    try {
      // Generate daily budgets for the range
      const start = new Date(bulkForm.start_date);
      const end = new Date(bulkForm.end_date);
      const daily_budgets = [];
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        daily_budgets.push({
          date: d.toISOString().split('T')[0],
          sales_budget: parseFloat(bulkForm.daily_sales_budget),
          labor_hours_budget: parseFloat(bulkForm.daily_labor_hours_budget)
        });
      }
      
      const res = await axios.post(`${API}/budgets/bulk`, {
        location_id: selectedLocation,
        start_date: bulkForm.start_date,
        end_date: bulkForm.end_date,
        daily_budgets
      });
      
      toast.success(`Created ${res.data.created} budgets, skipped ${res.data.skipped} existing`);
      setShowBulkDialog(false);
      setBulkForm({ start_date: '', end_date: '', daily_sales_budget: '', daily_labor_hours_budget: '' });
      fetchBudgets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create budgets');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (budget) => {
    setEditingBudget(budget);
    setSingleForm({
      date: budget.date,
      sales_budget: budget.sales_budget.toString(),
      labor_hours_budget: budget.labor_hours_budget.toString()
    });
    setShowSingleDialog(true);
  };

  const handleDelete = async (budgetId) => {
    if (!confirm('Are you sure you want to delete this budget?')) return;
    
    try {
      await axios.delete(`${API}/budgets/${budgetId}`);
      toast.success('Budget deleted');
      fetchBudgets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete budget');
    }
  };

  const getWeekDates = () => {
    const startOfWeek = new Date();
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay() + (weekOffset * 7));
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(d.getDate() + i);
      dates.push(d);
    }
    return dates;
  };

  const getBudgetForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return budgets.find(b => b.date === dateStr);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  const selectedLocationData = locations.find(l => l.id === selectedLocation);
  const weekDates = getWeekDates();

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50">
      <Sidebar />
      
      <main className="flex-1 p-4 lg:p-8" data-testid="budget-management-page">
        {/* Header */}
        <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 font-['Outfit']">
              Budget Management
            </h1>
            <p className="text-slate-600 mt-1">
              Set daily sales and labor budgets in advance
            </p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              onClick={() => { setEditingBudget(null); setShowSingleDialog(true); }}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="add-single-budget-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Budget
            </Button>
            <Button 
              onClick={() => setShowBulkDialog(true)}
              variant="outline"
              data-testid="add-bulk-budget-btn"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Bulk Entry
            </Button>
          </div>
        </div>

        {/* Location Selector */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4 items-end">
              <div className="flex-1">
                <Label className="text-sm text-slate-600 mb-2 block">Select Location</Label>
                <select
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="w-full h-12 px-4 rounded-lg border border-slate-300 bg-white text-slate-900 font-medium focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  data-testid="location-select"
                >
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name} ({loc.code})
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Week Navigator */}
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => setWeekOffset(w => w - 1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm font-medium min-w-32 text-center">
                  {weekDates[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {weekDates[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </span>
                <Button variant="outline" size="icon" onClick={() => setWeekOffset(w => w + 1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
                {weekOffset !== 0 && (
                  <Button variant="ghost" size="sm" onClick={() => setWeekOffset(0)}>
                    Today
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Weekly Budget Grid */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-violet-600" />
              {selectedLocationData?.name} - Weekly Budgets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-2">
              {weekDates.map((date, i) => {
                const budget = getBudgetForDate(date);
                const isToday = date.toDateString() === new Date().toDateString();
                const isPast = date < new Date() && !isToday;
                
                return (
                  <div
                    key={i}
                    className={cn(
                      "p-3 rounded-lg border transition-colors",
                      isToday ? "border-violet-500 bg-violet-50" : "border-slate-200",
                      isPast && !budget ? "bg-red-50" : ""
                    )}
                  >
                    <div className="text-xs font-medium text-slate-500 mb-1">
                      {date.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className="text-sm font-semibold text-slate-900 mb-2">
                      {date.getDate()}
                    </div>
                    
                    {budget ? (
                      <div className="space-y-1">
                        <div className="flex items-center gap-1 text-xs">
                          <DollarSign className="w-3 h-3 text-emerald-500" />
                          <span className="font-mono">{formatCurrency(budget.sales_budget)}</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs">
                          <Clock className="w-3 h-3 text-blue-500" />
                          <span className="font-mono">{budget.labor_hours_budget}h</span>
                        </div>
                        <div className="flex gap-1 mt-2">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6"
                            onClick={() => handleEdit(budget)}
                          >
                            <Edit2 className="w-3 h-3" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6 text-red-500 hover:text-red-700"
                            onClick={() => handleDelete(budget.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-slate-400">
                        {isPast ? (
                          <Badge variant="outline" className="text-red-500 border-red-300">Missing</Badge>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full h-8 text-xs"
                            onClick={() => {
                              setEditingBudget(null);
                              setSingleForm({
                                date: date.toISOString().split('T')[0],
                                sales_budget: '',
                                labor_hours_budget: ''
                              });
                              setShowSingleDialog(true);
                            }}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Set
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Single Budget Dialog */}
        <Dialog open={showSingleDialog} onOpenChange={setShowSingleDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingBudget ? 'Edit Budget' : 'Create Budget'}</DialogTitle>
              <DialogDescription>
                Set the daily sales and labor hours budget for {selectedLocationData?.name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={singleForm.date}
                  onChange={(e) => setSingleForm({ ...singleForm, date: e.target.value })}
                  disabled={!!editingBudget}
                />
              </div>
              <div>
                <Label>Sales Budget ($)</Label>
                <Input
                  type="number"
                  placeholder="5000.00"
                  value={singleForm.sales_budget}
                  onChange={(e) => setSingleForm({ ...singleForm, sales_budget: e.target.value })}
                />
              </div>
              <div>
                <Label>Labor Hours Budget</Label>
                <Input
                  type="number"
                  placeholder="80"
                  value={singleForm.labor_hours_budget}
                  onChange={(e) => setSingleForm({ ...singleForm, labor_hours_budget: e.target.value })}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSingleDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateBudget} disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingBudget ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Budget Dialog */}
        <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Bulk Budget Entry</DialogTitle>
              <DialogDescription>
                Set the same budget for multiple days at {selectedLocationData?.name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={bulkForm.start_date}
                    onChange={(e) => setBulkForm({ ...bulkForm, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={bulkForm.end_date}
                    onChange={(e) => setBulkForm({ ...bulkForm, end_date: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Daily Sales Budget ($)</Label>
                <Input
                  type="number"
                  placeholder="5000.00"
                  value={bulkForm.daily_sales_budget}
                  onChange={(e) => setBulkForm({ ...bulkForm, daily_sales_budget: e.target.value })}
                />
              </div>
              <div>
                <Label>Daily Labor Hours Budget</Label>
                <Input
                  type="number"
                  placeholder="80"
                  value={bulkForm.daily_labor_hours_budget}
                  onChange={(e) => setBulkForm({ ...bulkForm, daily_labor_hours_budget: e.target.value })}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBulkDialog(false)}>Cancel</Button>
              <Button onClick={handleBulkCreate} disabled={submitting}>
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Budgets
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
